// ===========================================
// Registration fees not yet applied (#374 Phase 2)
// ===========================================
// Laura's checklist: the registration fees that are paid and waiting for a child's first invoice
// (Held), and the three lists the office must clear so none is missed: Check the child (matched to
// the family's only child though the names differ), Unassigned (paid, no child yet) and Not linked
// (paid, but not linked to a family yet: a booking not converted, or cancelled). A live snapshot; no dates to pick.
//
// Gated by registration_fees.read (the route guard's PERMISSION_SUBPATHS, the nav item, and the
// query's `enabled`), NOT by `reports` and NOT owner-only. "Assign to child" needs
// registration_fees.write. Registered in 4 of the nine report places (the #642 family credit
// report's shape); the Overview card, `reportKeys` and the digest are a follow-up.

import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { usePermissions } from '../../../hooks/usePermissions';
import { useExportRegistrationFeesNotApplied, useRegistrationFeesNotApplied } from '../../../hooks/useReports';
import type { NotAppliedFeeRow } from '../../../api/reports.api';
import AssignRegistrationFeeDialog from '../../../components/admin/families/AssignRegistrationFeeDialog';
import { formatCurrencyFromDollars } from '../../../utils/currency';
import { apiErrorMessage } from '../../../utils/apiError';

/** A @db.Date / school-day key (YYYY-MM-DD) as dd/mm/yyyy, without a Date object. */
const dayKeyToAu = (key: string | null) => (key ? key.split('-').reverse().join('/') : '');
const nowrap = { whiteSpace: 'nowrap' } as const;

const MATCH_LABEL: Record<string, string> = {
  FULL_NAME: 'Full name',
  FIRST_NAME: 'First name',
  ONLY_CHILD: 'Only child, names differ',
};

export default function RegistrationFeesNotAppliedPage() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const { can } = usePermissions();
  const canRead = can('registration_fees', 'read');
  const canWrite = can('registration_fees', 'write');
  const canSeeInvoices = can('operations_invoices', 'read');
  const { data, isLoading, isError, error } = useRegistrationFeesNotApplied(canRead);
  const exportMutation = useExportRegistrationFeesNotApplied();
  const [assigning, setAssigning] = useState<NotAppliedFeeRow | null>(null);

  if (!canRead) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">You do not have permission to open the registration fees report.</Alert>
      </Box>
    );
  }

  const money = (v: string) => formatCurrencyFromDollars(v);
  const base = `/${schoolSlug}/admin`;

  const familyCell = (r: NotAppliedFeeRow) =>
    r.parentId ? (
      <Link component={RouterLink} to={`${base}/families?parent=${encodeURIComponent(r.parentId)}`}>
        {r.familyName}
      </Link>
    ) : (
      <span>{r.familyName}</span>
    );

  const total = data ? data.totals.held + data.totals.check + data.totals.unassigned + data.totals.notLinked : 0;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Registration fees not yet applied
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fees paid and waiting for the child&apos;s first invoice, and the fees that need the office to
            check a child or a family.{data ? ` As of ${dayKeyToAu(data.asOf)}.` : ''}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={exportMutation.isPending ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={() => exportMutation.mutate()}
          disabled={!data || total === 0 || exportMutation.isPending}
          sx={{ minHeight: { xs: 44 } }}
        >
          Export CSV
        </Button>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">{apiErrorMessage(error, 'Could not load the registration fees report.')}</Alert>
      ) : !data ? null : (
        <Stack spacing={3}>
          {/* ---- Held ---- */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Held, waiting for the first invoice
              </Typography>
              {!data.creditOnFirstInvoice ? (
                <Alert severity="info" data-testid="switch-off-message">
                  The registration fee is not credited at this school (Settings → Onboarding → Registration fee).
                </Alert>
              ) : data.held.length === 0 ? (
                <Alert severity="success">No registration fee is waiting to be credited.</Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="held-totals">
                    {data.totals.held} {data.totals.held === 1 ? 'child' : 'children'}, {money(data.totals.heldAmount)} held
                    {data.creditFeesPaidFrom ? ` (fees paid from ${dayKeyToAu(data.creditFeesPaidFrom)})` : ''}
                    {alreadyInvoicedSentence(data.held)}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Child</TableCell>
                          <TableCell>Family</TableCell>
                          <TableCell sx={nowrap}>Paid</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right" sx={nowrap}>Days held</TableCell>
                          <TableCell>Next term</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.held.map((r) => (
                          <TableRow key={r.feeId} data-testid="held-row">
                            <TableCell sx={nowrap}>{r.studentName}</TableCell>
                            <TableCell sx={nowrap}>{familyCell(r)}</TableCell>
                            <TableCell sx={nowrap}>{dayKeyToAu(r.paidOn)}</TableCell>
                            <TableCell align="right" sx={nowrap} title={r.creditAmount !== r.amount ? `Paid ${money(r.amount)}; the credit is capped at the school fee` : undefined}>
                              {money(r.creditAmount)}
                            </TableCell>
                            <TableCell align="right">{r.daysHeld}</TableCell>
                            <TableCell sx={nowrap}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <span>{r.nextTerm ? r.nextTerm.name : r.childActive ? 'No current enrolment' : 'Child not active'}</span>
                                {r.alreadyInvoiced &&
                                  (canSeeInvoices && r.alreadyInvoiced.invoiceId ? (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      component={RouterLink}
                                      to={`${base}/invoices/${r.alreadyInvoiced.invoiceId}`}
                                      clickable
                                      label={alreadyInvoicedLabel(r.alreadyInvoiced)}
                                      title={r.alreadyInvoiced.invoiceNumber ? `Invoice ${r.alreadyInvoiced.invoiceNumber}` : undefined}
                                    />
                                  ) : (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      label={alreadyInvoicedLabel(r.alreadyInvoiced)}
                                      title={r.alreadyInvoiced.invoiceNumber ? `Invoice ${r.alreadyInvoiced.invoiceNumber}` : undefined}
                                    />
                                  ))}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>

          {/* ---- Check the child ---- */}
          <AssignSection
            title="Check the child"
            note="Matched to the family's only child, though the names differ. Confirm it is the right child, or choose another."
            emptyText="No fee needs its child checked."
            testId="check"
            rows={data.check}
            showMatch
            canAssign={canWrite}
            onAssign={setAssigning}
            familyCell={familyCell}
            money={money}
          />

          {/* ---- Unassigned ---- */}
          <AssignSection
            title="Unassigned"
            note="Paid, but not yet linked to a child."
            emptyText="Every paid fee has a child."
            testId="unassigned"
            rows={data.unassigned}
            canAssign={canWrite}
            onAssign={setAssigning}
            familyCell={familyCell}
            money={money}
          />

          {/* ---- Not linked ---- */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Not linked to a family
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Paid, but not linked to a family yet (a booking not converted, or cancelled). Convert the booking or
                link it to the family, or reconcile or refund a cancelled one.
              </Typography>
              {data.notLinked.length === 0 ? (
                <Alert severity="success">No payment is waiting without a family.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Child (Meet &amp; Greet)</TableCell>
                        <TableCell>Meet &amp; Greet status</TableCell>
                        <TableCell sx={nowrap}>Paid</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>What to do</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.notLinked.map((r) => (
                        <TableRow key={r.feeId} data-testid="not-linked-row">
                          <TableCell sx={nowrap}>{r.childName ?? ''}</TableCell>
                          <TableCell sx={nowrap}>{r.meetAndGreetStatus ? statusLabel(r.meetAndGreetStatus) : ''}</TableCell>
                          <TableCell sx={nowrap}>{dayKeyToAu(r.paidOn)}</TableCell>
                          <TableCell align="right" sx={nowrap}>{money(r.amount)}</TableCell>
                          <TableCell sx={nowrap}>
                            {r.meetAndGreetStatus === 'CANCELLED' ? 'Reconcile or refund' : 'Convert or link the booking'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}

      <AssignRegistrationFeeDialog
        open={assigning !== null}
        onClose={() => setAssigning(null)}
        familyId={assigning?.familyId ?? ''}
        feeId={assigning?.feeId ?? null}
        childOptions={assigning?.children ?? []}
        credit={assigning?.credit ?? null}
        currentStudentId={assigning?.studentId ?? null}
      />
    </Box>
  );
}

/** "CANCELLED" -> "Cancelled", "PENDING_VERIFICATION" -> "Pending verification". */
/**
 * What the office can actually DO about a child who is already invoiced (/qa 374 H1). A draft the child is
 * linked to can take the credit in Edit; any other invoice cannot (no tool links the fee to it), so the fee
 * comes off the child's NEXT invoice by itself and a hand-added $50 would be given twice.
 */
function alreadyInvoicedLabel(ref: { creditable: boolean }): string {
  return ref.creditable ? 'On a draft: add it in Edit' : "Comes off the next invoice, don't add $50 by hand";
}

function alreadyInvoicedSentence(held: Array<{ alreadyInvoiced: { creditable: boolean } | null }>): string {
  const invoiced = held.filter((r) => r.alreadyInvoiced);
  if (invoiced.length === 0) return '';
  const editable = invoiced.filter((r) => r.alreadyInvoiced?.creditable).length;
  const rest = invoiced.length - editable;
  const parts: string[] = [];
  if (editable > 0) parts.push(`${editable} on a draft invoice: open it, Edit, tick the registration fee credit`);
  if (rest > 0) parts.push(`${rest} already invoiced without it: the fee comes off the child's next invoice by itself, never add $50 by hand`);
  return `. ${parts.join('. ')}.`;
}

function statusLabel(status: string): string {
  const words = status.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

interface AssignSectionProps {
  title: string;
  note: string;
  emptyText: string;
  testId: string;
  rows: NotAppliedFeeRow[];
  showMatch?: boolean;
  canAssign: boolean;
  onAssign: (row: NotAppliedFeeRow) => void;
  familyCell: (r: NotAppliedFeeRow) => ReactNode;
  money: (v: string) => string;
}

function AssignSection({
  title,
  note,
  emptyText,
  testId,
  rows,
  showMatch,
  canAssign,
  onAssign,
  familyCell,
  money,
}: AssignSectionProps) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {note}
        </Typography>
        {rows.length === 0 ? (
          <Alert severity="success">{emptyText}</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {showMatch && <TableCell>Child</TableCell>}
                  <TableCell>Family</TableCell>
                  <TableCell sx={nowrap}>Paid</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  {showMatch && <TableCell>Matched by</TableCell>}
                  {canAssign && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.feeId} data-testid={`${testId}-row`}>
                    {showMatch && <TableCell sx={nowrap}>{r.studentName}</TableCell>}
                    <TableCell sx={nowrap}>{familyCell(r)}</TableCell>
                    <TableCell sx={nowrap}>{dayKeyToAu(r.paidOn)}</TableCell>
                    <TableCell align="right" sx={nowrap}>{money(r.amount)}</TableCell>
                    {showMatch && <TableCell sx={nowrap}>{r.studentMatch ? MATCH_LABEL[r.studentMatch] : ''}</TableCell>}
                    {canAssign && (
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                          {r.credit && (
                            <Chip
                              size="small"
                              color="info"
                              label={r.credit.invoiceNumber ? `Credited on ${r.credit.invoiceNumber}` : 'Credited on an invoice'}
                            />
                          )}
                          <Button size="small" onClick={() => onAssign(r)} sx={{ ...nowrap, minHeight: { xs: 44, sm: 'auto' } }}>
                            {r.credit ? 'Confirm child' : 'Assign to child'}
                          </Button>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
